import Filter from '@/src/components/organism/Filter';
import { useEffect, useMemo, useRef, useState } from 'react';
import { communityCommentFilters, communityFilters } from './filter';
import { useQuery } from '@tanstack/react-query';
import useQueryValues from '@/src/hooks/useQueryValues';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { AgGridReact } from 'ag-grid-react';
import Table from '@/src/components/atom/Table';
import {
  communityCommentListColumn,
  communityPostingListColumn,
} from './column';
import SectionCardNoneTitle from '@/src/components/molecule/SectionCardNoneTitle';
import Typo from '@/src/components/atom/Typo';
import Button from '@/src/components/atom/Button';
import usePaginationQuery from '@/src/hooks/usePaginationQuery';
import { NAV_CONST } from '@/src/constants/nav';
import Tabs from '@/src/components/molecule/Tabs';
import {
  getCommunityPostingCategoriesAPI,
  getCommunityPostingExcelAPI,
  getCommunityPostingsAPI,
} from '@/src/apis/rest/communityPosting';
import CommunityPostingDto from '@/src/models/CommunityPostingDto';
import { ListResponse } from '@/src/types/common';
import CommunityCommentDto from '@/src/models/CommunityCommentDto';
import {
  getCommunityCommentExcelAPI,
  getCommunityCommentsAPI,
} from '@/src/apis/rest/communityComment';
import CommunityCategoryModal from '@/src/pages/Community/components/CommunityCategoryModal';
import ExcelDownloadButton from '@/src/components/atom/ExcelDownloadButton';
import PATH from '@/src/routes/path';
import { useNavigate } from 'react-router-dom';

type ModalState = 'NONE' | 'COMMENT_CATEGORY';

export default function ComunityListPage() {
  const { defaultValues } = useQueryValues({
    createdAt: ['createdAtFrom', 'createdAtTo'],
  });
  const { page, pageSize, handlePageChange } = usePaginationQuery();
  const [tabType, setTabType] = useState<string>(
    (defaultValues as any)?.tabValue || 'community',
  );
  const navigate = useNavigate();

  const communityGridRef = useRef<AgGridReact>(null);
  const commentGridRef = useRef<AgGridReact>(null);
  const [values, setValues] = useState(defaultValues);
  const [modalState, setModalState] = useState<ModalState>('NONE');

  const getExcelAPI =
    tabType === 'community'
      ? getCommunityPostingExcelAPI
      : getCommunityCommentExcelAPI;
  const fileNamePrefix =
    tabType === 'community' ? '커뮤니티_게시글_리스트' : '커뮤니티_댓글_리스트';

  const {
    data: communityPostingCategories,
    refetch: refetchCommunityPostingCategories,
  } = useQuery({
    queryKey: ['community-posting-category-options'],
    queryFn: getCommunityPostingCategoriesAPI,
  });

  const communityPostingCategoryOptions = useMemo(() => {
    return [
      { label: '전체', value: '' },
      ...(communityPostingCategories || []).map(({ categoryName, id }) => ({
        label: categoryName,
        value: String(id),
      })),
    ];
  }, [communityPostingCategories]);

  const {
    data: tableData,
    isError,
    isFetching,
    isFetched,
  } = useQuery({
    queryKey: ['community-posting', values, page, pageSize, tabType],
    queryFn: async () => {
      let result: ListResponse<CommunityPostingDto | CommunityCommentDto> = {
        rows: [],
        count: 0,
      };

      switch (tabType) {
        case 'community':
          result = await getCommunityPostingsAPI({ ...values, page, pageSize });
          break;
        case 'comment':
          result = await getCommunityCommentsAPI({ ...values, page, pageSize });
          break;
        default:
          break;
      }

      return result;
    },
  });

  // event
  const handleOpenModal = (type: ModalState) => {
    setModalState(type);
  };
  const handleCloseModal = () => {
    setModalState('NONE');
  };
  const handleToggleModal = (type: ModalState) => {
    if (modalState === type) {
      handleCloseModal();
    } else {
      setModalState(type);
    }
  };

  const getFilters = () => {
    return tabType === 'community'
      ? communityFilters(communityPostingCategoryOptions || [])
      : communityCommentFilters();
  };

  // effect
  useEffect(() => {
    if (isFetching && communityGridRef.current) {
      communityGridRef.current.api?.showLoadingOverlay();
    }
    if (isFetching && commentGridRef.current) {
      commentGridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  return (
    <>
      <MainContentTitle
        pageTitle={NAV_CONST.communityManagement}
        breadcrumbList={[NAV_CONST.communityManagement]}
      />
      <div className="mb-20 flex flex-col gap-6">
        <Filter
          title="검색 필터"
          filters={getFilters()}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setValues(values);
          }}
        />
        <div className="flex flex-col gap-y-6">
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">커뮤니티 목록</Typo>
              <div className="flex gap-2">
                <ExcelDownloadButton
                  disabled={isError || !isFetched || tableData?.count === 0}
                  downloadExcelAPI={getExcelAPI}
                  searchParams={values}
                  fileName={fileNamePrefix}
                />
                <Button
                  buttonSize="medium"
                  buttonColor="secondary"
                  onClick={() => handleOpenModal('COMMENT_CATEGORY')}
                >
                  <div className="flex items-center gap-x-2">
                    게시글 카테고리 관리
                  </div>
                </Button>
              </div>
            </div>
            <Tabs
              defaultValue="community"
              onChange={e => {
                setTabType(e);
                handlePageChange(1, 10);
              }}
              hasSearchParams
              includeValues={['community', 'comment']}
            >
              <div className="mb-4">
                <Tabs.TabsList>
                  <Tabs.SingleTrigger value="community" label="게시글 목록" />
                  <Tabs.SingleTrigger value="comment" label="댓글 목록" />
                </Tabs.TabsList>
              </div>

              <Tabs.SingleContent value="community">
                <Table
                  ref={communityGridRef}
                  rowData={isError ? [] : tableData?.rows || []}
                  isPaginationDataMapping
                  columnDefs={communityPostingListColumn(e => {
                    navigate(
                      PATH.COMMUNITY_POSTING_DETAIL.replace(':id', String(e)),
                    );
                  })}
                  total={tableData?.count || 0}
                  page={page}
                  pageSize={pageSize}
                  handlePaginationClick={handlePageChange}
                />
              </Tabs.SingleContent>
              <Tabs.SingleContent value="comment">
                <Table
                  ref={commentGridRef}
                  rowData={isError ? [] : tableData?.rows || []}
                  isPaginationDataMapping
                  columnDefs={communityCommentListColumn(e => {
                    navigate(
                      PATH.COMMUNITY_COMMENT_DETAIL.replace(':id', String(e)),
                    );
                  })}
                  total={tableData?.count || 0}
                  page={page}
                  pageSize={pageSize}
                  handlePaginationClick={handlePageChange}
                />
              </Tabs.SingleContent>
            </Tabs>
          </SectionCardNoneTitle>
        </div>
      </div>
      {/* 모달 */}
      {modalState === 'COMMENT_CATEGORY' && (
        <CommunityCategoryModal
          open={modalState === 'COMMENT_CATEGORY'}
          onOpenChange={() => handleToggleModal('COMMENT_CATEGORY')}
          defaultCategories={communityPostingCategories || []}
          onSubmit={() => {
            refetchCommunityPostingCategories();
          }}
        />
      )}
    </>
  );
}
